import { ShieldAlert, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { useSafetyReview, useSafetyVerdict } from '@/api/endpoints/safety'
import { Button } from '@/components/ui/Button'
import { SafetyDetailPanel } from '@/features/safety/components/SafetyDetailPanel'
import { translateStatus } from '@/features/safety/lib/i18n'
import { errorMessage } from '@/lib/errors'
import styles from './SafetyDock.module.scss'

export interface SafetyDockProps {
  videoId: string
  lang: string
}

/**
 * Project Detail tab for safety: shows the existing read-only
 * :class:`SafetyDetailPanel` (verdict + audit log) plus an action card
 * with manual approve/reject buttons. The actions hit
 * ``POST /api/v1/safety/{video_id}/review`` — approving flips the row
 * to PASSED so the upload gate stops blocking; rejecting flips it to
 * BLOCKED and feeds the safety golden set on the backend.
 *
 * The action card is always rendered: even on a PASSED video the user
 * may want to manually flag it as BLOCKED, and on never-checked videos
 * the read-only panel already prompts for a rescan.
 */
export function SafetyDock({ videoId, lang }: SafetyDockProps) {
  const verdict = useSafetyVerdict({ videoId, lang })
  const review = useSafetyReview()
  const [reason, setReason] = useState('')

  const status = verdict.data?.status ?? 'never_checked'
  const isPassed = status === 'passed'
  const isBlocked = status === 'blocked'
  const isNeverChecked = status === 'never_checked'

  function submit(decision: 'approve' | 'reject') {
    const trimmed = reason.trim()
    review.mutate(
      trimmed ? { videoId, lang, decision, reason: trimmed } : { videoId, lang, decision },
      {
        onSuccess: (out) => {
          toast.success(
            decision === 'approve' ? 'Vídeo aprovado' : 'Vídeo reprovado',
            { description: `Status agora: ${translateStatus(out.status)}` },
          )
          setReason('')
        },
        onError: (err) => {
          toast.error('Falha ao registrar revisão', { description: errorMessage(err) })
        },
      },
    )
  }

  return (
    <div className={styles.shell}>
      <div className={styles.actionCard}>
        <div className={styles.actionHead}>
          <h3 className={styles.actionTitle}>Revisão manual</h3>
          <p className={styles.actionHint}>
            {isNeverChecked
              ? 'Este vídeo ainda não passou pelo scan automático. Rode um rescan pelo botão do cabeçalho antes de decidir manualmente.'
              : 'Aprovar libera o upload mesmo que alguma camada tenha bloqueado. Reprovar trava o upload e guarda o caso para o time avaliar.'}
          </p>
        </div>

        <div className={styles.reason}>
          <label className={styles.reasonLabel} htmlFor="safety-review-reason">
            Motivo (opcional — fica salvo no histórico)
          </label>
          <textarea
            id="safety-review-reason"
            className={styles.reasonInput}
            value={reason}
            placeholder="Ex.: contexto educacional — sem violência explícita"
            onChange={(e) => {
              setReason(e.target.value)
            }}
            disabled={review.isPending}
          />
        </div>

        <div className={styles.buttons}>
          <Button
            variant="secondary"
            leadingIcon={<ShieldCheck />}
            onClick={() => {
              submit('approve')
            }}
            disabled={review.isPending || isPassed || isNeverChecked}
          >
            {isPassed ? 'Já aprovado' : 'Aprovar'}
          </Button>
          <Button
            variant="secondary"
            leadingIcon={<ShieldAlert />}
            onClick={() => {
              submit('reject')
            }}
            disabled={review.isPending || isBlocked || isNeverChecked}
          >
            {isBlocked ? 'Já reprovado' : 'Reprovar'}
          </Button>
        </div>

        {review.isError ? (
          <span className={styles.errorText}>{errorMessage(review.error)}</span>
        ) : null}
      </div>

      <SafetyDetailPanel videoId={videoId} lang={lang} />
    </div>
  )
}
